import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { GameType, GetInviteFriendResponse } from '@/modules/promotion/type'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Button } from '@/common/ui/button'
import { Switch } from '@/common/ui/switch'
import { FormProvider } from 'react-hook-form'
import useMutationUpdateInviteFriend from '@/modules/promotion/api/hooks/useMutateUpdateInviteFriend'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'

import Spinner from '@/common/components/Spinner'
import { SelectedLevelsField, TurnoverField } from '../../../CommonField'
import { CreditType, CalculateInterval } from '@/schema'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useQueryClient } from '@tanstack/react-query'
import { InviteFriendQueryKeys } from '@/modules/promotion/keys'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

const GAME_TYPES = [
	GameType.SLOT,
	GameType.CASINO,
	GameType.LOTTO,
	GameType.SPORT,
	GameType.FISHING,
] as const

const GAME_TYPE_LABELS: Record<GameType, string> = {
	[GameType.SLOT]: 'สล็อต',
	[GameType.CASINO]: 'คาสิโน',
	[GameType.LOTTO]: 'หวย',
	[GameType.SPORT]: 'กีฬา',
	[GameType.FISHING]: 'ยิงปลา',
}

const rewardSchema = z.object({
	ratio: z
		.number()
		.min(0, 'เปอร์เซ็นต์ต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
	maxCreditAmount: z
		.number()
		.min(0, 'เครดิตสูงสุดต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
})

const returnLossRewardSchema = z.object({
	creditRatio: z
		.number()
		.min(0, 'อัตราส่วนต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
	maxCreditAmount: z
		.number()
		.min(0, 'เครดิตสูงสุดต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
})

const inviteFriendSchema = z.object({
	minimumFirstDepositAmount: z.number().min(0, 'กรุณากรอกยอดฝากขั้นต่ำ'),
	firstDepositCreditType: z.nativeEnum(CreditType),
	turnoverRound: z.number().min(0, 'กรุณากรอกรอบเทิร์นโอเวอร์'),
	firstDepositCreditAmount: z
		.number()
		.min(0, 'จำนวนเครดิตต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
	firstDepositCreditRatio: z
		.number()
		.min(0, 'เปอร์เซ็นต์จ่ายเครดิตต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
	firstDepositMaxCreditAmount: z
		.number()
		.min(0, 'เครดิตสูงสุดต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
	isRequireFirstDepositPromotion: z.boolean(),
	selectedLevels: z.number().min(1).max(10),
	commissionTiers: z.array(
		z.object({
			level: z.number().optional(),
			rewards: z.record(rewardSchema),
			returnLossRewards: z.record(returnLossRewardSchema).optional(),
			depositCommissionRatio: z
				.number()
				.min(0, 'อัตราส่วนต้องมากกว่าหรือเท่ากับ 0')
				.max(1, 'อัตราส่วนต้องไม่เกิน 100%')
				.optional()
				.nullable(),
			depositCommissionMaxAmount: z
				.number()
				.min(0, 'เครดิตสูงสุดต้องมากกว่าหรือเท่ากับ 0')
				.optional()
				.nullable(),
		}),
	),
	isReturnLossEnabled: z.boolean(),
	returnLossMinimumLossAmount: z
		.number()
		.min(0, 'ยอดขาดทุนขั้นต่ำต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
	returnLossCalculateInterval: z
		.nativeEnum(CalculateInterval)
		.optional()
		.nullable(),
	isDepositCommissionEnabled: z.boolean(),
	isRequireDepositPromotion: z.boolean(),
	depositCommissionMinimumAmount: z
		.number()
		.min(0, 'ยอดฝากขั้นต่ำต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
})

type InviteFriendFormValues = z.infer<typeof inviteFriendSchema>

export default function InviteFriendBox({
	defaultValue,
	isLoading,
}: {
	isLoading: boolean
	defaultValue?: GetInviteFriendResponse
}) {
	const { data: user } = useQueryProfile()
	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_INVITE,
	)

	const queryClient = useQueryClient()

	const { mutate: updateInviteFriend } = useMutationUpdateInviteFriend({
		onSuccess: () => {
			toast.success('ตั้งค่าการชวนเพื่อนสำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [InviteFriendQueryKeys.GetInviteFriend],
			})
		},
		onError: (error) => {
			showErrorToast(error)
		},
	})

	const form = useForm<InviteFriendFormValues>({
		resolver: zodResolver(inviteFriendSchema),
		defaultValues: defaultValue
			? {
					minimumFirstDepositAmount: defaultValue.minimumFirstDepositAmount,
					firstDepositCreditType: defaultValue.firstDepositCreditType,
					turnoverRound: defaultValue.turnoverRound,
					firstDepositCreditAmount: defaultValue.firstDepositCreditAmount,
					firstDepositCreditRatio: defaultValue.firstDepositCreditRatio,
					firstDepositMaxCreditAmount: defaultValue.firstDepositMaxCreditAmount,
					isRequireFirstDepositPromotion:
						defaultValue.isRequireFirstDepositPromotion,
					selectedLevels: defaultValue.commissionTiers.length,
					isReturnLossEnabled: defaultValue.isReturnLossEnabled,
					returnLossMinimumLossAmount: defaultValue.returnLossMinimumLossAmount,
					returnLossCalculateInterval: defaultValue.returnLossCalculateInterval,
					isDepositCommissionEnabled: defaultValue.isDepositCommissionEnabled,
					isRequireDepositPromotion: defaultValue.isRequireDepositPromotion,
					depositCommissionMinimumAmount:
						defaultValue.depositCommissionMinimumAmount,
					commissionTiers: defaultValue.commissionTiers.map((tier) => ({
						level: tier.level,
						rewards: GAME_TYPES.reduce(
							(acc, type) => ({
								...acc,
								[type]: {
									ratio: tier.rewards[type]?.ratio || 0,
									maxCreditAmount: tier.rewards[type]?.maxCreditAmount || 0,
								},
							}),
							{},
						),
						returnLossRewards: GAME_TYPES.reduce(
							(acc, type) => ({
								...acc,
								[type]: {
									creditRatio: tier.returnLossRewards?.[type]?.creditRatio || 0,
									maxCreditAmount:
										tier.returnLossRewards?.[type]?.maxCreditAmount || 0,
								},
							}),
							{},
						),
						depositCommissionRatio: tier.depositCommissionRatio ?? null,
						depositCommissionMaxAmount: tier.depositCommissionMaxAmount ?? null,
					})),
				}
			: undefined,
	})

	if (!defaultValue || isLoading) return <Spinner />

	const handleSubmit = (values: InviteFriendFormValues) => {
		const { selectedLevels, ...rest } = values

		const commissionTiers = Array.from({ length: selectedLevels }).map(
			(_, index) => ({
				level: index + 1,
				rewards: GAME_TYPES.reduce(
					(acc, type) => ({
						...acc,
						[type]: {
							ratio: values.commissionTiers[index]?.rewards[type]?.ratio ?? 0,
							maxCreditAmount:
								values.commissionTiers[index]?.rewards[type]?.maxCreditAmount ??
								0,
						},
					}),
					{},
				),
				returnLossRewards: GAME_TYPES.reduce(
					(acc, type) => ({
						...acc,
						[type]: {
							creditRatio:
								values.commissionTiers[index]?.returnLossRewards?.[type]
									?.creditRatio ?? 0,
							maxCreditAmount:
								values.commissionTiers[index]?.returnLossRewards?.[type]
									?.maxCreditAmount ?? 0,
						},
					}),
					{},
				),
				depositCommissionRatio:
					values.commissionTiers[index]?.depositCommissionRatio ?? 0,
				depositCommissionMaxAmount:
					values.commissionTiers[index]?.depositCommissionMaxAmount ?? 0,
			}),
		)

		const payload = {
			payload: {
				...rest,
				firstDepositCreditAmount: rest.firstDepositCreditAmount ?? 0,
				firstDepositCreditRatio: rest.firstDepositCreditRatio ?? 0,
				firstDepositMaxCreditAmount: rest.firstDepositMaxCreditAmount ?? 0,
				isRequireFirstDepositPromotion: rest.isRequireFirstDepositPromotion,
				isReturnLossEnabled: rest.isReturnLossEnabled,
				returnLossMinimumLossAmount: rest.returnLossMinimumLossAmount ?? 0,
				returnLossCalculateInterval:
					rest.returnLossCalculateInterval ?? CalculateInterval.DAILY,
				isDepositCommissionEnabled: rest.isDepositCommissionEnabled,
				isRequireDepositPromotion: rest.isRequireDepositPromotion,
				depositCommissionMinimumAmount:
					rest.depositCommissionMinimumAmount ?? 0,
				commissionTiers,
			},
		}

		updateInviteFriend(payload)
	}

	const creditType = form.watch('firstDepositCreditType')
	const selectedLevels = form.watch('selectedLevels')
	const isReturnLossEnabled = form.watch('isReturnLossEnabled')
	const isDepositCommissionEnabled = form.watch('isDepositCommissionEnabled')

	return (
		<FormProvider {...form}>
			<form
				onSubmit={form.handleSubmit(handleSubmit, showFormValidationToast)}
				className="space-y-4 sm:space-y-6"
			>
				{/* Main Form Fields - Mobile Responsive Grid */}
				<FormField
					control={form.control}
					name="isRequireFirstDepositPromotion"
					render={({ field }) => (
						<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
							<div className="space-y-0.5">
								<FormLabel className="text-base">
									ฝากแรกต้องติดโปรโมชั่น
								</FormLabel>
								<div className="text-sm text-muted-foreground">
									เพื่อนที่ชวนจะต้องฝากแบบติดโปรโมชั่นเท่านั้น
									ถึงจะได้รับคอมมิชชั่นฝากแรก (แนะนำให้เปิดไว้
									เพื่อป้องกันไม่ให้ลูกค้ารับรางวัลฟรีโดยชวนเพื่อนมาฝากและถอนทันทีโดยไม่ได้เล่น)
								</div>
							</div>
							<FormControl>
								<Switch
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
						</FormItem>
					)}
				/>

				{/* Return Loss Settings */}
				<FormField
					control={form.control}
					name="isReturnLossEnabled"
					render={({ field }) => (
						<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
							<div className="space-y-0.5">
								<FormLabel className="text-base">
									เปิดใช้งานคืนยอดเสียชวนเพื่อน
								</FormLabel>
								<div className="text-sm text-muted-foreground">
									เปิดให้ผู้ชวนได้รับส่วนแบ่งจากยอดเสียของเพื่อนที่ชวน
								</div>
							</div>
							<FormControl>
								<Switch
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
						</FormItem>
					)}
				/>

				{isReturnLossEnabled && (
					<div className="grid grid-cols-1 gap-4 rounded-lg border p-4 sm:grid-cols-2 sm:gap-6">
						<FormField
							control={form.control}
							name="returnLossMinimumLossAmount"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										ยอดเสียขั้นต่ำ (รวมของลูกสายทุกคนและทุกเกม
										จะไม่ได้คอมมิชชั่นเลยถ้ายอดเสียน้อยกว่าค่านี้)
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											{...field}
											value={
												field.value !== null && field.value !== undefined
													? field.value
													: ''
											}
											className="bg-background"
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
											min="0"
											step="1"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="returnLossCalculateInterval"
							render={({ field }) => (
								<FormItem>
									<FormLabel>รอบเวลาการคำนวณ</FormLabel>
									<Select
										onValueChange={field.onChange}
										value={field.value ?? undefined}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="เลือกรอบการคำนวณ" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value={CalculateInterval.DAILY}>
												รายวัน
											</SelectItem>
											<SelectItem value={CalculateInterval.WEEKLY}>
												รายสัปดาห์
											</SelectItem>
											<SelectItem value={CalculateInterval.MONTHLY}>
												รายเดือน
											</SelectItem>
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300 sm:col-span-2">
							💡 การตั้งค่าเปอร์เซ็นต์และคอมมิชชั่นสูงสุดของคืนยอดเสียชวนเพื่อน
							สามารถตั้งสำหรับแต่ละลำดับขั้นได้ในส่วนด้านล่าง
						</div>
					</div>
				)}

				{/* Deposit Commission Settings */}
				<FormField
					control={form.control}
					name="isDepositCommissionEnabled"
					render={({ field }) => (
						<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
							<div className="space-y-0.5">
								<FormLabel className="text-base">
									เปิดใช้งานคอมมิชชั่นจากยอดฝาก
								</FormLabel>
								<div className="text-sm text-muted-foreground">
									เปิดให้ผู้ชวนได้รับส่วนแบ่งทุกครั้งที่เพื่อนทำการฝากเงิน
								</div>
							</div>
							<FormControl>
								<Switch
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
						</FormItem>
					)}
				/>

				{isDepositCommissionEnabled && (
					<div className="grid grid-cols-1 gap-4 rounded-lg border p-4 sm:grid-cols-2 sm:gap-6">
						<FormField
							control={form.control}
							name="isRequireDepositPromotion"
							render={({ field }) => (
								<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 sm:col-span-2">
									<div className="space-y-0.5">
										<FormLabel className="text-base">
											ฝากต้องติดโปรโมชั่น
										</FormLabel>
										<div className="text-sm text-muted-foreground">
											เพื่อนต้องฝากแบบติดโปรโมชั่นเท่านั้นถึงจะได้รับคอมมิชชั่นฝาก
											(แนะนำให้เปิดไว้เพื่อป้องกันการฝากและถอนทันที)
										</div>
									</div>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="depositCommissionMinimumAmount"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										ยอดฝากขั้นต่ำสำหรับคอมมิชชั่นฝาก
										(เพื่อนต้องฝากมากกว่าหรือเท่ากับยอดนี้ถึงจะได้คอมมิชชั่น)
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											{...field}
											value={
												field.value !== null && field.value !== undefined
													? field.value
													: ''
											}
											className="bg-background"
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
											min="0"
											step="1"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300 sm:col-span-2">
							💡 การตั้งค่าเปอร์เซ็นต์และคอมมิชชั่นสูงสุดของคอมมิชชั่นยอดฝาก
							สามารถตั้งสำหรับแต่ละลำดับขั้นได้ในส่วนด้านล่าง
						</div>
					</div>
				)}

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
					<FormField
						control={form.control}
						name="minimumFirstDepositAmount"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									ยอดฝากขั้นต่ำครั้งแรก
									เพื่อนต้องฝากมากกว่าหรือเท่ากับยอดนี้ถึงจะได้คอมมิชชั่นฝากแรก
								</FormLabel>
								<FormControl>
									<Input
										type="number"
										{...field}
										onChange={(e) => field.onChange(Number(e.target.value))}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="firstDepositCreditType"
						render={({ field }) => (
							<FormItem>
								<FormLabel>ประเภทรางวัลเครดิตฝากครั้งแรก</FormLabel>
								<Select
									onValueChange={field.onChange}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder="เลือกประเภท" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										<SelectItem value="FIXED">Fixed</SelectItem>
										<SelectItem value="RATIO">Ratio</SelectItem>
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					{creditType === 'FIXED' && (
						<FormField
							control={form.control}
							name="firstDepositCreditAmount"
							render={({ field }) => (
								<FormItem>
									<FormLabel>จำนวนรางวัลเครดิตฝากแรก</FormLabel>
									<FormControl>
										<Input
											type="number"
											{...field}
											value={
												field.value !== null && field.value !== undefined
													? field.value
													: ''
											}
											className="bg-background"
											onChange={(e) => field.onChange(Number(e.target.value))}
											min="0"
											step="0.01"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{creditType === 'RATIO' && (
						<>
							<FormField
								control={form.control}
								name="firstDepositCreditRatio"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											เปอร์เซ็นต์รางวัลเครดิตฝากแรก (0-100%)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												{...field}
												value={
													field.value !== null && field.value !== undefined
														? String(Number((field.value * 100).toFixed(4)))
														: ''
												}
												className="bg-background"
												onChange={(e) => {
													const value = e.target.value
														? parseFloat(e.target.value)
														: null
													field.onChange(value !== null ? value / 100 : null)
												}}
												min={0}
												max={100}
												step="0.01"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="firstDepositMaxCreditAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											เครดิตสูงสุด ในกรณีที่ให้รางวัลเครดิตเป็นอัตราส่วน
											จะได้มากสุดที่ยอดนี้
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												{...field}
												value={
													field.value !== null && field.value !== undefined
														? String(Number(field.value.toFixed(2)))
														: ''
												}
												className="bg-background"
												onChange={(e) => {
													const value = e.target.value
														? Math.max(0, Number(e.target.value))
														: null
													field.onChange(value)
												}}
												min="0"
												step="1"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</>
					)}

					<TurnoverField control={form.control} />

					<SelectedLevelsField control={form.control} />
				</div>

				{/* Commission Tiers - Mobile Responsive */}
				{Array.from({ length: selectedLevels }).map((_, index) => (
					<div key={index} className="border-border/50 bg-background/50">
						<div className="pt-4 sm:pt-6">
							<h3 className="mb-2 text-base font-semibold sm:text-lg">
								Level {index + 1}
							</h3>
							<p className="mb-4 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
								* อัตราส่วนแบ่งเครดิตจะคิดเมื่อเพื่อนเล่นแบบไม่ฝากโปร
							</p>

							{/* Game Types Grid - Mobile Responsive */}
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:gap-8">
								{GAME_TYPES.map((gameType) => (
									<div
										key={gameType}
										className="space-y-3 rounded-lg border border-border/50 bg-background/80 p-4 sm:space-y-4 sm:p-6"
									>
										<h4 className="text-sm font-semibold tracking-wide sm:text-base">
											{GAME_TYPE_LABELS[gameType]}
										</h4>

										{/* Form Fields - Stack vertically to avoid alignment issues */}
										<div className="space-y-3 sm:space-y-4">
											<div className="space-y-3 sm:space-y-4">
												<h5 className="text-xs font-medium">
													คอมมิชชั่นจากยอดเทิร์น
												</h5>
												<FormField
													control={form.control}
													name={`commissionTiers.${index}.rewards.${gameType}.ratio`}
													render={({ field }) => (
														<FormItem>
															<FormLabel className="text-xs text-muted-foreground sm:text-sm">
																เปอร์เซ็นต์ค่าคอมมิชชั่นยอดเทิร์น
																(แนะนำให้ใส่น้อยกว่า 1 เช่น 0.7)
															</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	{...field}
																	value={
																		field.value !== null &&
																		field.value !== undefined
																			? String(
																					Number(
																						(field.value * 100).toFixed(4),
																					),
																				)
																			: ''
																	}
																	className="bg-background text-sm"
																	onChange={(e) => {
																		const value = e.target.value
																			? parseFloat(e.target.value)
																			: null
																		field.onChange(
																			value !== null ? value / 100 : null,
																		)
																	}}
																	min={0}
																	max={100}
																	step="0.01"
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name={`commissionTiers.${index}.rewards.${gameType}.maxCreditAmount`}
													render={({ field }) => (
														<FormItem>
															<FormLabel className="text-xs text-muted-foreground sm:text-sm">
																คอมมิชชั่นสูงสุดต่อเดือน (เมื่อได้ถึงยอดนี้แล้ว
																จะไม่ได้คอมมิชชั่นจากเกมชนิดนี้อีกในเดือนนั้น)
															</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	{...field}
																	value={
																		field.value !== null &&
																		field.value !== undefined
																			? String(Number(field.value.toFixed(2)))
																			: ''
																	}
																	className="bg-background text-sm"
																	onChange={(e) => {
																		const value = e.target.value
																			? Math.max(0, Number(e.target.value))
																			: null
																		field.onChange(value)
																	}}
																	min="0"
																	step="1"
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>

											{isReturnLossEnabled && (
												<div className="space-y-3 border-t pt-3 sm:space-y-4 sm:pt-4">
													<h5 className="text-xs font-medium">
														คอมมิชชั่นจากยอดเสียของเพื่อน
													</h5>
													<FormField
														control={form.control}
														name={`commissionTiers.${index}.returnLossRewards.${gameType}.creditRatio`}
														render={({ field }) => (
															<FormItem>
																<FormLabel className="text-xs text-muted-foreground sm:text-sm">
																	เปอร์เซ็นต์ค่าคอมมิชชั่นยอดเสียชวนเพื่อน
																	(0-100%)
																</FormLabel>
																<FormControl>
																	<Input
																		type="number"
																		{...field}
																		value={
																			field.value !== null &&
																			field.value !== undefined
																				? String(
																						Number(
																							(field.value * 100).toFixed(4),
																						),
																					)
																				: ''
																		}
																		className="bg-background text-sm"
																		onChange={(e) => {
																			const value = e.target.value
																				? parseFloat(e.target.value)
																				: null
																			field.onChange(
																				value !== null ? value / 100 : null,
																			)
																		}}
																		min={0}
																		max={100}
																		step="0.01"
																	/>
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>
													<FormField
														control={form.control}
														name={`commissionTiers.${index}.returnLossRewards.${gameType}.maxCreditAmount`}
														render={({ field }) => (
															<FormItem>
																<FormLabel className="text-xs text-muted-foreground sm:text-sm">
																	คอมมิชชั่นสูงสุดต่อรอบ
																	(เมื่อได้ถึงยอดนี้แล้วจะไม่ได้คอมมิชชั่นจากเกมชนิดนี้อีกในรอบนั้น)
																</FormLabel>
																<FormControl>
																	<Input
																		type="number"
																		{...field}
																		value={
																			field.value !== null &&
																			field.value !== undefined
																				? String(Number(field.value.toFixed(2)))
																				: ''
																		}
																		className="bg-background text-sm"
																		onChange={(e) => {
																			const value = e.target.value
																				? Math.max(0, Number(e.target.value))
																				: null
																			field.onChange(value)
																		}}
																		min="0"
																		step="1"
																	/>
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											)}
										</div>
									</div>
								))}
							</div>

							{/* Deposit Commission per tier - single fields, not per game type */}
							{isDepositCommissionEnabled && (
								<div className="mt-4 space-y-3 rounded-lg border border-border/50 bg-background/80 p-4 sm:space-y-4 sm:p-6">
									<h5 className="text-sm font-medium">คอมมิชชั่นจากยอดฝาก</h5>
									<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
										<FormField
											control={form.control}
											name={`commissionTiers.${index}.depositCommissionRatio`}
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-xs text-muted-foreground sm:text-sm">
														เปอร์เซ็นต์ค่าคอมมิชชั่นจากยอดฝาก (0-100%)
													</FormLabel>
													<FormControl>
														<Input
															type="number"
															{...field}
															value={
																field.value !== null &&
																field.value !== undefined
																	? String(
																			Number((field.value * 100).toFixed(4)),
																		)
																	: ''
															}
															className="bg-background text-sm"
															onChange={(e) => {
																const value = e.target.value
																	? parseFloat(e.target.value)
																	: null
																field.onChange(
																	value !== null ? value / 100 : null,
																)
															}}
															min={0}
															max={100}
															step="0.01"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name={`commissionTiers.${index}.depositCommissionMaxAmount`}
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-xs text-muted-foreground sm:text-sm">
														คอมมิชชั่นสูงสุดต่อการฝากหนึ่งครั้ง
													</FormLabel>
													<FormControl>
														<Input
															type="number"
															{...field}
															value={
																field.value !== null &&
																field.value !== undefined
																	? String(Number(field.value.toFixed(2)))
																	: ''
															}
															className="bg-background text-sm"
															onChange={(e) => {
																const value = e.target.value
																	? Math.max(0, Number(e.target.value))
																	: null
																field.onChange(value)
															}}
															min="0"
															step="1"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>
							)}
						</div>
					</div>
				))}

				{/* Submit Button - Mobile Responsive */}
				<div className="flex gap-4">
					<Button
						disabled={!hasWritePermission}
						type="submit"
						variant="default"
						className="flex-1"
						size="lg"
					>
						บันทึก
					</Button>
				</div>
			</form>
		</FormProvider>
	)
}
